/**
 * EOB cart drawer and cart page (replaces CornerCart).
 *
 * One custom element, <eob-cart-drawer>, used by sections/eob-cart-drawer (a slide-in drawer)
 * and sections/eob-cart-page (data-page: the /cart page, always "open").
 *
 * - Watches every cart request on the page (fetch and XHR to /cart/add|change|update|clear), so
 *   any add-to-cart button, app or custom section refreshes the drawer, and adds open it.
 * - Line items and totals are server-rendered by sections/eob-cart-drawer.liquid.
 * - Rules come from the shop metafield eob_cart.config (edited in the EOB Cart app) and are
 *   evaluated here: progress bar, free samples, gifts with purchase, gift messages.
 */
(() => {
  if (window.EobCart) return;

  const CART_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|#|$)/;
  const OWN_HEADER = 'X-EOB-Cart';
  const store = {
    get(k) { try { return sessionStorage.getItem(`eob-cart:${k}`); } catch (e) { return null; } },
    set(k, v) { try { sessionStorage.setItem(`eob-cart:${k}`, v); } catch (e) { /* ignore */ } }
  };
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ---------- watch cart requests from anything on the page ---------- */

  let pending = null;
  function notify(kind) {
    const drawer = document.querySelector('eob-cart-drawer');
    if (!drawer) return;
    pending = pending || { open: false };
    if (kind === 'add') pending.open = true;
    clearTimeout(notify.timer);
    notify.timer = setTimeout(() => {
      const opts = pending;
      pending = null;
      drawer.refresh({ open: opts.open, added: opts.open });
    }, 60);
  }

  const isOwn = (headers) => {
    if (!headers) return false;
    if (typeof headers.get === 'function') return !!headers.get(OWN_HEADER);
    return !!headers[OWN_HEADER];
  };

  const nativeFetch = window.fetch;
  window.fetch = function eobFetch(input, init) {
    const result = nativeFetch.apply(this, arguments);
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || String(input);
      const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      const match = method === 'POST' && url.match(CART_RE);
      if (match && !isOwn(init && init.headers) && !isOwn(input && input.headers)) {
        result.then((res) => { if (res.ok) notify(match[1]); }).catch(() => {});
      }
    } catch (e) { /* never break the caller */ }
    return result;
  };

  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function eobOpen(method, url) {
    this.eobRequest = [String(method).toUpperCase(), String(url)];
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function eobSend() {
    const req = this.eobRequest;
    const match = req && req[0] === 'POST' && req[1].match(CART_RE);
    if (match) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) notify(match[1]);
      });
    }
    return xhrSend.apply(this, arguments);
  };

  /* ---------- helpers ---------- */

  /**
   * Whole pounds/dollars, like the store's money format (no decimals). Amounts still to spend
   * round up, so "spend £38 more" never leaves a customer a few pence short.
   */
  function formatMoney(cents, currency, roundUp = false) {
    const units = roundUp ? Math.ceil(cents / 100) : Math.round(cents / 100);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(units);
    } catch (e) {
      return String(units);
    }
  }

  function fill(text, tokens) {
    return String(text || '').replace(/\{(\w+)\}/g, (m, key) => (key in tokens ? tokens[key] : m));
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Same default as DEFAULT_NOTICE_REACHED in the EOB Cart app (lib/config.ts).
  const DEFAULT_NOTICE_REACHED = 'Your bag qualifies for {benefits}. <a href="/account/login">Log in</a> or <a href="/account/register">sign up</a> to unlock.';

  const ICONS = {
    // Solid padlock, same shape as CornerCart's.
    lock: '<svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 1a5 5 0 0 0-5 5v4H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5zm-3 9V6a3 3 0 0 1 6 0v4H9z"/></svg>',
    // Drawn so its visual weight sits in the middle of the circle, not low-left.
    check: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 11.5l4 4 8-9"/></svg>',
    // Steps not reached yet (members): what the step gives, like CornerCart's.
    truck: '<svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M21.89 11.55 20.17 8.1A2 2 0 0 0 18.38 7H17V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h1.18a3 3 0 0 0 5.64 0h2.36a3 3 0 0 0 5.64 0H20a2 2 0 0 0 2-2v-4a1 1 0 0 0-.11-.45ZM8 18a1 1 0 1 1 1-1 1 1 0 0 1-1 1Zm8 0a1 1 0 1 1 1-1 1 1 0 0 1-1 1Zm4-2h-1.17a3 3 0 0 0-1.83-1.83V9h1.38L20 12.24Z"/></svg>',
    gift: '<svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20 13v7a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-7h16zM14.5 2a3.5 3.5 0 0 1 3.163 5.001L21 7a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1l3.337.001a3.5 3.5 0 0 1 5.664-3.95A3.48 3.48 0 0 1 14.5 2zm-5 2a1.5 1.5 0 0 0-.144 2.993L9.5 7H11V5.5a1.5 1.5 0 0 0-1.356-1.493L9.5 4zm5 0-.144.007a1.5 1.5 0 0 0-1.35 1.349L13 5.5V7h1.5l.144-.007a1.5 1.5 0 0 0 0-2.986L14.5 4z"/></svg>',
    chevronLeft: '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>',
    chevronRight: '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>'
  };

  /* ---------- the drawer ---------- */

  class EobCartDrawer extends HTMLElement {
    connectedCallback() {
      this.isPage = this.hasAttribute('data-page');
      this.sectionId = this.dataset.sectionId;
      this.sections = `${this.sectionId},cart-icon-bubble`;
      this.panel = this.querySelector('.eob-cart__panel') || this;
      this.picker = this.querySelector('[data-eob-picker]');
      this.reminder = this.querySelector('[data-eob-reminder]');
      this.recs = this.querySelector('[data-eob-recs]');
      this.queue = Promise.resolve();
      this.recsCache = {};

      try {
        this.config = JSON.parse(this.querySelector('[data-eob-config]').textContent) || {};
      } catch (e) {
        this.config = {};
      }
      this.readContext();

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('submit', this.onSubmit.bind(this));
      this.addEventListener('keydown', (evt) => {
        if (evt.key === 'Escape') this.closeTopmost();
        if (evt.key === 'Tab') this.trapFocus(evt);
      });

      // Every way to the cart page opens the drawer instead: the bag icon, menu links, app
      // "view cart" links... Modified clicks (new tab/window) are left alone.
      const cartPath = ((window.theme && theme.routes && theme.routes.cart) || `${root()}cart`).replace(/\/$/, '');
      const isCartLink = (a) => {
        try {
          const url = new URL(a.getAttribute('href'), window.location.href);
          return url.origin === window.location.origin && url.pathname.replace(/\/$/, '') === cartPath && !url.search;
        } catch (e) {
          return false;
        }
      };
      document.addEventListener('click', (evt) => {
        if (evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;
        const trigger = evt.target.closest('#cart-icon, [data-eob-open-cart], .js-open-cart-drawer, a[href]');
        if (!trigger) return;
        const explicit = trigger.matches('#cart-icon, [data-eob-open-cart], .js-open-cart-drawer');
        if (!explicit && !isCartLink(trigger)) return;
        evt.preventDefault();
        // Nothing else gets this click: older helpers would otherwise still navigate to /cart.
        evt.stopPropagation();
        this.open(trigger);
      }, true);

      if (!this.isPage) {
        // Thumbnails are lazy (the drawer is hidden on load). Start them when the shopper heads
        // for the bag, so they're there when it opens.
        const warm = (evt) => {
          if (evt.target.closest && evt.target.closest('#cart-icon')) this.warmImages();
        };
        document.addEventListener('pointerover', warm, { passive: true });
        document.addEventListener('focusin', warm);

        // The theme's "after adding to cart: go to the cart page" option must not redirect.
        if (window.theme && theme.settings && theme.settings.afterAtc === 'page') theme.settings.afterAtc = 'drawer';
        // A cart click caught by the inline script in the layout before this file loaded.
        if (window.EobCartPending) {
          window.EobCartPending = false;
          this.open();
        }
      }
      if (this.isPage) {
        this.loadRecommendations();
      }
      document.addEventListener('dispatch:cart-drawer:open', () => this.open());
      document.addEventListener('dispatch:cart-drawer:close', () => this.close());
      document.addEventListener('dispatch:cart-drawer:refresh', () => this.refresh());
      document.addEventListener('cart:refresh', () => this.refresh());
      window.addEventListener('pageshow', (evt) => { if (evt.persisted) this.refresh(); });
      // Back from another tab: pick up changes made there (the cart page and an open drawer).
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.isOpen) this.refresh();
      });

      const progressSlot = this.querySelector('[data-eob-progress]');
      if (progressSlot && window.ResizeObserver) {
        let lastWidth = 0;
        new ResizeObserver(([entry]) => {
          const width = Math.round(entry.contentRect.width);
          if (width && width !== lastWidth) {
            lastWidth = width;
            this.fitProgress();
          }
        }).observe(progressSlot);
      }

      this.afterRender();
      // Tier or market may have changed since the cart was built (e.g. after logging in).
      this.enqueue(() => this.reconcile());
    }

    /* ----- context ----- */

    readContext() {
      try {
        this.ctx = JSON.parse(this.querySelector('[data-eob-context]').textContent);
      } catch (e) {
        this.ctx = { items: [], total: 0, triggers: {}, giftAvailable: {}, texts: {} };
      }
      this.ctx.items = this.ctx.items || [];
      this.ctx.declinedList = String(this.ctx.declined || '').split(',').filter(Boolean);
    }

    get rate() {
      const r = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
      return r > 0 ? r : 1;
    }

    /** Converts a configured amount (in `currency`) to cart cents. */
    toCents(amount, currency) {
      const { currency: cartCurrency, shopCurrency } = this.ctx;
      let cents = Math.round(Number(amount) * 100);
      if (currency && currency !== cartCurrency && currency === shopCurrency) {
        cents = Math.round(cents * this.rate);
      }
      return cents;
    }

    money(cents, roundUp = false) {
      return formatMoney(Math.max(0, cents), this.ctx.currency, roundUp);
    }

    get progressRule() {
      const list = this.config.progress || [];
      const { market } = this.ctx;
      // "Member perks for everyone" (EOB Cart app): logged-out shoppers get the members' bar,
      // samples and delivery messages while it's on.
      const audience = this.ctx.audience === 'guest' && this.config.perksForEveryone ? 'member' : this.ctx.audience;
      return list.find((p) => p.market === market && p.audience === audience)
        || (audience !== 'guest' && list.find((p) => p.market === market && p.audience === 'member'))
        || null;
    }

    /** Progress state for the current cart. */
    progressState() {
      const rule = this.progressRule;
      if (!rule) return null;
      const total = this.ctx.total;
      const milestones = (rule.milestones || []).map((m) => {
        const cents = this.toCents(m.amount, m.currency);
        return { ...m, cents, reached: total >= cents };
      });
      return { rule, milestones, locked: !!rule.locked };
    }

    get samplesConfig() {
      return { max: 3, autoOpen: true, ...(this.config.samples || {}) };
    }

    samplesUnlocked(state = this.progressState()) {
      if (!state || state.locked) return false;
      // A step at 0 means "from the first item": it still needs something paid in the bag,
      // never just samples or gifts on their own.
      if (this.ctx.regularCount === 0) return false;
      return state.milestones.some((m) => m.type === 'samples' && m.reached);
    }

    /** A locked (logged-out) bar whose samples step the bag has reached: samples need logging in. */
    samplesLockedReached(state = this.progressState()) {
      if (!state || !state.locked || this.ctx.regularCount === 0) return false;
      return state.milestones.some((m) => m.type === 'samples' && m.reached);
    }

    sampleLines() {
      return this.ctx.items.filter((i) => i.kind === 'sample');
    }

    /* ----- gifts ----- */

    /** Which gift variant each campaign wants in the cart right now. */
    desiredGifts() {
      const { market, total, triggers, giftAvailable } = this.ctx;
      const desired = [];
      if (this.ctx.regularCount === 0) return desired;

      (this.config.gifts || []).forEach((gift) => {
        if (!gift.enabled) return;
        if (gift.markets && gift.markets.length && !gift.markets.includes(market)) return;
        if (gift.audiences && gift.audiences.length && !gift.audiences.includes(this.ctx.audience)) return;
        const levels = gift.levels || [];
        let level = null;

        if (gift.trigger && gift.trigger.type === 'collection') {
          if ((triggers[gift.id] || 0) >= (gift.trigger.minQuantity || 1)) [level] = levels;
        } else if (gift.trigger && gift.trigger.type === 'spend') {
          levels.forEach((l) => {
            const min = l.min && l.min[market];
            const max = l.max && l.max[market];
            if (min == null) return;
            if (total >= Math.round(min * 100) && (max == null || max === '' || total <= Math.round(max * 100))) level = l;
          });
        }

        if (level && giftAvailable[String(level.variantId)]) {
          desired.push({ gift, level, variantId: Number(level.variantId) });
        }
      });
      return desired;
    }

    managedGiftIds() {
      const ids = new Set();
      (this.config.gifts || []).forEach((g) => (g.levels || []).forEach((l) => ids.add(Number(l.variantId))));
      return ids;
    }

    /* ----- reconcile cart with the rules ----- */

    /**
     * Adds/removes gifts and removes samples that are no longer unlocked. Also enforces the
     * limits however the bag was changed (product page, another tab, the Ajax API): at most
     * `max` samples, one of each, quantity 1; one line per gift, quantity 1; no free gift
     * products outside a live campaign. Returns true when the cart was changed.
     */
    async reconcile(depth = 0) {
      if (depth > 2) return false;
      const items = this.ctx.items;
      const updates = {};
      const adds = [];
      let declined = this.ctx.declinedList.slice();
      let attrsChanged = false;

      // Samples: only while unlocked, and never more than allowed. The bag lists the newest line
      // first, so the oldest choices are kept.
      if (!this.samplesUnlocked()) {
        this.sampleLines().forEach((line) => { updates[line.key] = 0; });
      } else {
        const seen = new Set();
        const kept = [];
        this.sampleLines().slice().reverse().forEach((line) => {
          if (seen.has(line.variantId) || kept.length >= this.samplesConfig.max) {
            updates[line.key] = 0;
            return;
          }
          seen.add(line.variantId);
          kept.push(line);
          if (line.quantity > 1) updates[line.key] = 1;
        });
      }

      // Gifts.
      const managed = this.managedGiftIds();
      const desired = this.desiredGifts();
      const wanted = new Map(desired.map((d) => [d.variantId, d]));
      const giftSeen = new Set();
      items.filter((i) => managed.has(i.variantId)).slice().reverse().forEach((line) => {
        if (!wanted.has(line.variantId) || giftSeen.has(line.variantId)) {
          updates[line.key] = 0;
        } else {
          giftSeen.add(line.variantId);
          if (line.quantity > 1) updates[line.key] = 1;
        }
      });
      // A free gift product that isn't part of a live campaign (e.g. added from its page).
      items.filter((i) => i.kind === 'gift' && !managed.has(i.variantId) && i.linePrice === 0)
        .forEach((line) => { updates[line.key] = 0; });
      wanted.forEach((d, variantId) => {
        const inCart = items.some((i) => i.variantId === variantId);
        if (!inCart && !declined.includes(String(variantId))) {
          adds.push({ id: variantId, quantity: 1, properties: { _eob_gift: d.gift.id } });
        }
      });

      // A fresh bag forgets which gifts were removed by the customer.
      if (this.ctx.regularCount === 0 && declined.length) {
        declined = [];
        attrsChanged = true;
      }

      const hasUpdates = Object.keys(updates).length > 0;
      if (!hasUpdates && !adds.length && !attrsChanged) return false;

      if (hasUpdates || attrsChanged) {
        const body = { updates };
        if (attrsChanged) body.attributes = { _eob_declined: declined.join(',') };
        await this.request('update', body);
      }
      if (adds.length) await this.request('add', { items: adds });
      await this.reconcile(depth + 1);
      return true;
    }

    /* ----- network ----- */

    enqueue(task) {
      this.queue = this.queue.then(task).catch((err) => console.error('[eob-cart]', err)); // eslint-disable-line no-console
      return this.queue;
    }

    async request(action, body) {
      const res = await fetch(`${root()}cart/${action}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', [OWN_HEADER]: '1' },
        body: JSON.stringify({ ...body, sections: this.sections, sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok) {
        const err = new Error(data.description || data.message || this.ctx.texts.error);
        err.data = data;
        throw err;
      }
      if (data.sections) this.render(data.sections);
      else await this.fetchSections();
      return data;
    }

    async fetchSections() {
      // The cart page's section only exists on /cart; the drawer is on every page.
      const path = this.isPage ? window.location.pathname : root();
      const res = await fetch(`${path}?sections=${this.sections}`, { headers: { [OWN_HEADER]: '1' } });
      if (!res.ok) throw new Error(res.status);
      this.render(await res.json());
    }

    /** Re-renders from the server, applies rules, optionally opens. */
    refresh({ open = false, added = false } = {}) {
      return this.enqueue(async () => {
        await this.fetchSections();
        await this.reconcile();
        if (open) this.open();
        if (added) this.maybeAutoOpenPicker();
      });
    }

    /* ----- rendering ----- */

    render(sections) {
      const html = sections[this.sectionId];
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        this.querySelectorAll('[data-eob-region]').forEach((region) => {
          const fresh = doc.querySelector(`[data-eob-region="${region.dataset.eobRegion}"]`);
          if (fresh) region.innerHTML = fresh.innerHTML;
        });
        const ctx = doc.querySelector('[data-eob-context]');
        if (ctx) this.querySelector('[data-eob-context]').textContent = ctx.textContent;
        this.readContext();
        this.afterRender();
      }

      const bubbleHtml = sections['cart-icon-bubble'];
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubbleHtml && bubble) {
        const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        const fresh = doc.getElementById('cart-icon-bubble') || doc.querySelector('.shopify-section');
        if (fresh) bubble.innerHTML = fresh.innerHTML;
      }
    }

    afterRender() {
      const state = this.progressState();
      this.renderProgress(state);
      this.renderDelivery(state);
      this.renderSamplesBlock(state);
      this.renderGiftMessages();
      // Recommendations cost a request, so only load them while the drawer is visible.
      if (this.isOpen) this.loadRecommendations();
    }

    renderProgress(state) {
      const el = this.querySelector('[data-eob-progress]');
      if (!el) return;
      if (!state || !state.milestones.length || !this.ctx.itemCount) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      const { milestones, locked, rule } = state;
      const total = this.ctx.total;
      const n = milestones.length;
      const pos = (i) => ((i + 1) / n) * 100;

      let fillPct = 100;
      const nextIndex = milestones.findIndex((m) => !m.reached);
      if (nextIndex !== -1) {
        const prevCents = nextIndex === 0 ? 0 : milestones[nextIndex - 1].cents;
        const prevPos = nextIndex === 0 ? 0 : pos(nextIndex - 1);
        const span = milestones[nextIndex].cents - prevCents;
        const ratio = span > 0 ? Math.min(1, Math.max(0, (total - prevCents) / span)) : 1;
        fillPct = prevPos + ratio * (pos(nextIndex) - prevPos);
      }

      let message = '';
      let msgClass = '';
      if (locked) {
        // Once the bag has passed a step, say what it qualifies for; until then, the general notice.
        const reachedMessage = this.lockedReachedMessage(rule, milestones);
        message = reachedMessage || rule.notice || '';
        msgClass = ` eob-progress__msg--notice${reachedMessage ? ' eob-progress__msg--reached' : ''}`;
        // "Log in / Sign up…" (from the first link on) goes on its own line: always once a step is
        // passed, on phones for the general notice (eob-cart.css).
        const cta = message.indexOf('<a');
        if (cta > 0) message = `${message.slice(0, cta).trim()} <span class="eob-progress__cta">${message.slice(cta)}</span>`;
      } else if (nextIndex !== -1) {
        const next = milestones[nextIndex];
        const tokens = { remaining: this.money(next.cents - total, true), threshold: this.money(next.cents, true) };
        message = escapeHtml(fill(next.before, tokens));
        if (!message && nextIndex > 0) message = escapeHtml(milestones[nextIndex - 1].after || '');
      } else {
        message = escapeHtml(milestones[n - 1].after || '');
      }

      const dots = milestones.map((m, i) => {
        let icon = '';
        if (locked) icon = ICONS.lock;
        else if (m.reached) icon = ICONS.check;
        else icon = m.type === 'samples' ? ICONS.gift : ICONS.truck;
        // Locked bars still mark passed steps (they're coloured differently, like CornerCart's).
        const cls = `eob-progress__dot${m.reached ? ' is-reached' : ''}`;
        return `<li class="${cls}" style="--eob-pos:${pos(i)}%"><span class="eob-progress__marker">${icon}</span><span class="eob-progress__label">${escapeHtml(m.label || '')}</span></li>`;
      }).join('');

      el.innerHTML = `
        ${message ? `<p class="eob-progress__msg${msgClass}">${message}</p>` : ''}
        <div class="eob-progress${locked ? ' is-locked' : ''}" style="--eob-steps:${n}">
          <div class="eob-progress__track"><div class="eob-progress__fill" style="width:${fillPct}%"></div></div>
          <ol class="eob-progress__dots" role="list">${dots}</ol>
        </div>`;
      this.fitProgress();
    }

    /**
     * Step labels are positioned absolutely, so the bar can't size itself to them. Make room for
     * exactly the tallest label (one line or two), measured once the drawer is visible.
     */
    fitProgress() {
      const bar = this.querySelector('.eob-progress');
      if (!bar || !bar.offsetParent) return;
      const top = bar.getBoundingClientRect().top;
      const track = bar.querySelector('.eob-progress__track').getBoundingClientRect();
      const bottoms = [...bar.querySelectorAll('.eob-progress__label')].map((l) => l.getBoundingClientRect().bottom - top);
      if (!bottoms.length) return;
      bar.style.paddingBottom = `${Math.ceil(Math.max(...bottoms) - (track.bottom - top))}px`;
    }

    /**
     * "Your bag qualifies for free next day delivery and 3 free samples. Log in or sign up to
     * unlock." for a locked (logged-out) bar: the best delivery step passed, plus samples. The
     * text is the bar's "Notice once the bag passes a step" in the EOB Cart app (a bar saved
     * before that existed gets the default; an empty one keeps the general notice).
     */
    lockedReachedMessage(rule, milestones) {
      const template = rule.noticeReached === undefined ? DEFAULT_NOTICE_REACHED : rule.noticeReached;
      const reached = milestones.filter((m) => m.reached);
      if (!template || !reached.length) return '';
      const parts = [];
      const delivery = reached.filter((m) => m.type === 'delivery').pop();
      // "Free Standard Delivery (£75)" reads as "free delivery"; other delivery steps keep their name.
      if (delivery) parts.push((delivery.label || '').replace(/\s*\(.*?\)\s*/g, ' ').replace(/\bstandard\s+/i, '').trim().toLowerCase());
      if (reached.some((m) => m.type === 'samples')) parts.push(fill(this.samplesConfig.lockedText || '{max} free samples', { max: this.samplesConfig.max }));
      return fill(template, { benefits: escapeHtml(parts.filter(Boolean).join(' and ')) });
    }

    renderDelivery(state) {
      const el = this.querySelector('[data-eob-delivery]');
      if (!el) return;
      const { texts } = this.ctx;
      if (!state || state.locked) {
        el.textContent = this.ctx.audience === 'guest' ? texts.deliveryGuest : texts.deliveryCalculated;
      } else {
        const free = state.milestones.some((m) => m.type === 'delivery' && m.reached);
        el.textContent = free ? texts.deliveryFree : texts.deliveryCalculated;
      }
    }

    /** Samples as slots: chosen samples as cards, then empty "Add sample" slots. */
    renderSamplesBlock(state) {
      const locked = this.querySelector('[data-eob-sample-locked]');
      if (locked) locked.hidden = !this.samplesLockedReached(state);

      const el = this.querySelector('[data-eob-sample-slots]');
      if (!el) return;
      if (!this.samplesUnlocked(state)) {
        el.hidden = true;
        return;
      }
      const cfg = this.samplesConfig;
      const row = el.querySelector('[data-eob-slots-row]');
      row.querySelectorAll('.eob-slot--empty').forEach((slot) => slot.remove());
      const chosen = row.querySelectorAll('[data-eob-slot]').length;
      const label = escapeHtml(row.dataset.addLabel || 'Add sample');
      for (let i = chosen; i < cfg.max; i++) {
        row.insertAdjacentHTML('beforeend', `
          <button type="button" class="eob-slot eob-slot--empty" data-eob-open-picker>
            <span class="eob-slot__plus" aria-hidden="true"><svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"><path d="M6 1.5v9M1.5 6h9"/></svg></span>
            <span class="eob-slot__add">${label}</span>
          </button>`);
      }
      row.style.setProperty('--eob-slots', Math.max(cfg.max, chosen));
      // Shorter while nothing is chosen; full height once the first sample is in.
      row.classList.toggle('is-empty', chosen === 0);
      el.querySelector('[data-eob-slots-count]').textContent = fill(cfg.chosenText, { count: chosen, max: cfg.max });
      const change = el.querySelector('[data-eob-slots-change]');
      if (change) change.hidden = chosen === 0;
      el.hidden = false;
    }

    renderGiftMessages() {
      const { market, total } = this.ctx;
      (this.config.gifts || []).forEach((gift) => {
        (gift.levels || []).forEach((level, index) => {
          if (!level.message) return;
          const line = this.querySelector(`.eob-line[data-variant-id="${level.variantId}"] [data-eob-line-msg]`);
          if (!line) return;
          const next = (gift.levels || [])[index + 1];
          const nextMin = next && next.min && next.min[market];
          const tokens = {
            remaining: nextMin != null ? this.money(Math.round(nextMin * 100) - total, true) : '',
            next_threshold: nextMin != null ? this.money(Math.round(nextMin * 100)) : ''
          };
          line.textContent = fill(level.message, tokens);
          line.hidden = false;
        });
      });
    }

    async loadRecommendations() {
      if (!this.recs) return;
      const regular = this.ctx.items.filter((i) => i.kind === 'regular');
      if (!regular.length) {
        this.recs.hidden = true;
        return;
      }
      // Recommend for the newest paid item, but don't swap the row for the product just added
      // from the row itself (that would replace the list before the shopper sees "Added").
      const keep = this.justAddedProduct && regular.some((i) => String(i.productId) === String(this.recsProductId));
      const productId = keep ? this.recsProductId : regular[0].productId;
      this.recsProductId = productId;
      try {
        if (!this.recsCache[productId]) {
          const url = `${this.dataset.recsUrl}?product_id=${productId}&limit=10&intent=complementary&section_id=eob-cart-recs`;
          this.recsCache[productId] = fetch(url, { headers: { [OWN_HEADER]: '1' } }).then((r) => r.text());
        }
        const html = await this.recsCache[productId];
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const list = doc.querySelector('.eob-recs__list');
        // Hide anything that went into the bag since the list was fetched; the card just added
        // stays for a moment to confirm it, then fades out.
        const inCart = new Set(this.ctx.items.map((i) => String(i.productId)));
        const justAdded = this.justAddedProduct;
        if (list) this.renderRecsGiftTags(list);
        if (list) list.querySelectorAll('.eob-recs__item').forEach((item) => {
          if (!inCart.has(item.dataset.productId)) return;
          if (item.dataset.productId === justAdded) this.markAdded(item);
          else item.remove();
        });
        if (!list || !list.children.length) {
          this.recs.hidden = true;
          return;
        }
        const prev = this.recs.querySelector('.eob-recs__list');
        const scrollLeft = prev ? prev.scrollLeft : 0;
        this.recs.innerHTML = `
          <div class="eob-recs__head">
            <p class="eob-recs__heading" role="heading" aria-level="3">${escapeHtml(this.dataset.recsHeading || '')}</p>
            <div class="eob-recs__nav" hidden>
              <button type="button" class="eob-recs__arrow" data-eob-recs-step="-1" aria-label="Previous products">${ICONS.chevronLeft}</button>
              <button type="button" class="eob-recs__arrow" data-eob-recs-step="1" aria-label="More products">${ICONS.chevronRight}</button>
            </div>
          </div>`;
        this.recs.appendChild(list);
        this.recs.hidden = false;
        list.scrollLeft = scrollLeft;
        list.addEventListener('scroll', () => this.updateRecsNav(), { passive: true });
        this.updateRecsNav();
        const leaving = list.querySelector('.eob-recs__item.is-added');
        if (leaving) {
          this.justAddedProduct = null;
          setTimeout(() => {
            leaving.classList.add('is-leaving');
            setTimeout(() => {
              leaving.remove();
              if (!list.children.length) this.recs.hidden = true;
              this.updateRecsNav();
            }, 300);
          }, 1200);
        }
      } catch (e) {
        this.recs.hidden = true;
      }
    }

    /**
     * "Free gift" tags on recommended products: shown while adding the product would bring a
     * collection gift into the bag (live in this market, in stock, not already in the bag, and
     * not removed by the shopper).
     */
    renderRecsGiftTags(list) {
      const { market, audience, giftAvailable } = this.ctx;
      const inBag = new Set(this.ctx.items.map((i) => String(i.variantId)));
      const declined = new Set(this.ctx.declinedList);
      const offered = new Set();
      (this.config.gifts || []).forEach((gift) => {
        if (!gift.enabled || !gift.trigger || gift.trigger.type !== 'collection') return;
        if (gift.markets && gift.markets.length && !gift.markets.includes(market)) return;
        if (gift.audiences && gift.audiences.length && !gift.audiences.includes(audience)) return;
        const ids = (gift.levels || []).map((l) => String(l.variantId));
        if (!ids.length || !giftAvailable[ids[0]]) return;
        if (ids.some((id) => inBag.has(id) || declined.has(id))) return;
        offered.add(gift.id);
      });
      list.querySelectorAll('[data-eob-recs-gift]').forEach((tag) => {
        tag.hidden = !tag.dataset.eobRecsGift.split(' ').some((id) => offered.has(id));
      });
    }

    markAdded(item) {
      item.classList.add('is-added');
      const btn = item.querySelector('[data-eob-recs-form] button');
      if (btn) {
        btn.disabled = true;
        btn.innerHTML = `${escapeHtml(btn.dataset.addedLabel || 'Added')} ${ICONS.check}`;
      }
    }

    /** Shows the row's arrows only when it overflows, and greys out the end you're at. */
    updateRecsNav() {
      const list = this.recs && this.recs.querySelector('.eob-recs__list');
      const nav = this.recs && this.recs.querySelector('.eob-recs__nav');
      if (!list || !nav) return;
      const max = list.scrollWidth - list.clientWidth;
      nav.hidden = max <= 2;
      nav.querySelector('[data-eob-recs-step="-1"]').disabled = list.scrollLeft <= 2;
      nav.querySelector('[data-eob-recs-step="1"]').disabled = list.scrollLeft >= max - 2;
    }

    /**
     * One card per click, to an exact card position (not "one card from here"), with snapping
     * paused while it moves: iOS Safari can snap back or skip a card when a smooth scroll and
     * mandatory snapping meet, especially straight after a swipe.
     */
    scrollRecs(direction) {
      const list = this.recs.querySelector('.eob-recs__list');
      const card = list && list.querySelector('.eob-recs__item');
      if (!card) return;
      const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
      const step = card.getBoundingClientRect().width + gap;
      const max = list.scrollWidth - list.clientWidth;
      const index = Math.round(list.scrollLeft / step);
      const left = Math.max(0, Math.min(max, (index + direction) * step));
      clearTimeout(this.recsSnapTimer);
      list.style.scrollSnapType = 'none';
      list.scrollTo({ left, behavior: 'smooth' });
      // Snapping back on once the move has finished ('scrollend', or a timeout where unsupported).
      const restore = () => {
        clearTimeout(this.recsSnapTimer);
        list.removeEventListener('scrollend', restore);
        list.style.scrollSnapType = '';
      };
      list.addEventListener('scrollend', restore, { once: true });
      this.recsSnapTimer = setTimeout(restore, 600);
    }

    warmImages() {
      this.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    }

    /* ----- open / close ----- */

    get isOpen() {
      return this.isPage || (!this.hidden && this.classList.contains('is-open'));
    }

    open(opener) {
      if (this.isPage) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (opener) this.opener = opener;
      if (this.isOpen) return;
      this.warmImages();
      // The bag may have changed in another tab since this page loaded.
      this.refresh();
      this.hidden = false;
      document.documentElement.classList.add('eob-cart-open');
      void this.offsetWidth; // eslint-disable-line no-void
      this.classList.add('is-open');
      this.panel.focus({ preventScroll: true });
      this.loadRecommendations();
      document.dispatchEvent(new CustomEvent('on:cart-drawer:after-open'));
    }

    close() {
      if (this.isPage) {
        this.closeModal(this.picker);
        this.closeModal(this.reminder);
        return;
      }
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('eob-cart-open');
      this.closeModal(this.picker);
      this.closeModal(this.reminder);
      setTimeout(() => { if (!this.classList.contains('is-open')) this.hidden = true; }, 300);
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('on:cart-drawer:after-close'));
    }

    closeTopmost() {
      if (!this.reminder.hidden) this.closeModal(this.reminder);
      else if (!this.picker.hidden) this.cancelPicker();
      else this.close();
    }

    trapFocus(evt) {
      const scope = [this.reminder, this.picker].find((m) => !m.hidden) || (this.isPage ? null : this.panel);
      if (!scope) return;
      const focusable = [...scope.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      }
    }

    openModal(modal) {
      modal.hidden = false;
      const focusTarget = modal.querySelector('button, input');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }

    closeModal(modal) {
      if (modal) modal.hidden = true;
    }

    /* ----- samples picker ----- */

    async openPicker() {
      const cfg = this.samplesConfig;
      this.picker.querySelector('[data-eob-picker-title]').textContent = fill(cfg.title, { max: cfg.max });
      this.picker.querySelector('[data-eob-picker-text]').textContent = fill(cfg.text, { max: cfg.max });
      this.openModal(this.picker);

      const listEl = this.picker.querySelector('[data-eob-picker-list]');
      if (!this.samplesHtml) {
        this.samplesHtml = fetch(`${this.dataset.samplesUrl}?section_id=eob-cart-samples`, { headers: { [OWN_HEADER]: '1' } })
          .then((r) => (r.ok ? r.text() : Promise.reject(r.status)));
      }
      try {
        const html = await this.samplesHtml;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const list = doc.querySelector('.eob-samples');
        listEl.innerHTML = '';
        if (list) listEl.appendChild(list);
      } catch (e) {
        this.samplesHtml = null;
        listEl.textContent = this.ctx.texts.error;
      }

      // Remember each sample's place in the collection order, and tick the ones in the bag
      // (in bag order, so they're listed first in the order they were chosen).
      const chosenIds = this.sampleLines().map((l) => String(l.variantId));
      listEl.querySelectorAll('.eob-samples__item').forEach((item, index) => {
        item.dataset.index = index;
        const box = item.querySelector('[data-eob-sample]');
        box.checked = chosenIds.includes(box.value);
        item.dataset.chosenAt = box.checked ? chosenIds.indexOf(box.value) : '';
      });
      this.groupPicker(listEl);
      // Changing samples already in the bag: keep the chosen ones pinned at the top, so they're
      // easy to untick. Otherwise only when the "Pin chosen samples" setting is on.
      this.pickerPinned = 'pinChosen' in this.dataset || chosenIds.length > 0;
      this.arrangePicker();
      listEl.scrollTop = 0;
      this.updatePickerCount();
    }

    /**
     * Samples from brands already in the bag go first, under their own heading; the rest follow
     * under a second heading. Without a match the list stays as one, with no headings. Within
     * the first group, the brand named in the "brand listed last" setting (Biologique Recherche,
     * which is in most bags) comes after the other brands; when it's the only brand matched,
     * there's no group at all.
     */
    groupPicker(listEl) {
      const all = listEl.querySelector('.eob-samples');
      if (!all) return;
      all.dataset.eobPickerGroup = 'all';
      const brands = new Set(this.ctx.items
        .filter((i) => i.kind === 'regular' && i.vendor)
        .map((i) => i.vendor.trim().toLowerCase()));
      const items = [...all.querySelectorAll('.eob-samples__item')];
      const vendorOf = (item) => (item.querySelector('.eob-samples__vendor')?.textContent || '').trim().toLowerCase();
      const last = (this.picker.dataset.brandLast || '').trim().toLowerCase();
      const matched = items.filter((item) => brands.has(vendorOf(item)));
      const matches = [
        ...matched.filter((item) => vendorOf(item) !== last),
        ...matched.filter((item) => vendorOf(item) === last)
      ];
      items.forEach((item) => { item.dataset.group = 'all'; });
      // No group when the only brand matched is the one listed last (Biologique Recherche).
      if (!matches.length || matches.every((item) => vendorOf(item) === last)) return;
      // Number the rows in their new order, so a sample unticked later goes back to its place.
      [...matches, ...items.filter((item) => !matched.includes(item))]
        .forEach((item, index) => { item.dataset.index = index; });

      const heading = (text, group) => {
        const p = document.createElement('p');
        p.className = 'eob-samples__group';
        p.dataset.eobPickerHeading = group;
        p.textContent = text;
        return p;
      };
      const mine = document.createElement('ul');
      mine.className = 'eob-samples';
      mine.setAttribute('role', 'list');
      mine.dataset.eobPickerGroup = 'brands';
      matches.forEach((item) => { item.dataset.group = 'brands'; mine.appendChild(item); });
      all.before(heading(this.picker.dataset.brandsHeading || '', 'brands'), mine, heading(this.picker.dataset.allHeading || '', 'all'));
      this.updatePickerHeadings(listEl);
    }

    /** Hides a group heading while its list is empty (e.g. every match is pinned at the top). */
    updatePickerHeadings(listEl) {
      listEl.querySelectorAll('[data-eob-picker-heading]').forEach((h) => {
        const list = listEl.querySelector(`[data-eob-picker-group="${h.dataset.eobPickerHeading}"]`);
        h.hidden = !list || !list.children.length;
      });
    }

    /**
     * Chosen samples are pinned at the top of the picker (a sticky list the rest scroll under),
     * in the order they were picked; the rest keep the collection order. The rows below the
     * pinned block stay put while one moves in or out (scroll is compensated). Only when the
     * picker opened with samples already in the bag, or the section's "Pin chosen samples"
     * setting is on; otherwise the list keeps the collection order.
     */
    arrangePicker(moved) {
      if (!this.pickerPinned) return;
      const listEl = this.picker.querySelector('[data-eob-picker-list]');
      const groups = [...listEl.querySelectorAll('[data-eob-picker-group]')];
      if (!groups.length) return;
      let pinned = listEl.querySelector('.eob-samples--chosen');
      if (!pinned) {
        pinned = document.createElement('ul');
        pinned.className = 'eob-samples eob-samples--chosen';
        pinned.setAttribute('role', 'list');
        listEl.prepend(pinned);
      }
      const unpinned = () => groups.flatMap((g) => [...g.children]);
      const items = [...pinned.children, ...unpinned()];

      // The first row showing under the pinned block stays where it is.
      const pinnedBottom = () => pinned.getBoundingClientRect().bottom;
      const anchor = unpinned().find((i) => i !== moved && i.getBoundingClientRect().bottom > pinnedBottom());
      const offset = anchor ? anchor.getBoundingClientRect().top - pinnedBottom() : 0;

      const isChosen = (i) => i.querySelector('[data-eob-sample]').checked;
      items.filter(isChosen)
        .sort((a, b) => Number(a.dataset.chosenAt) - Number(b.dataset.chosenAt))
        .forEach((i) => { i.classList.add('is-chosen'); pinned.appendChild(i); });
      // Unchosen samples go back to their own group (brands in the bag, or all), in collection order.
      items.filter((i) => !isChosen(i))
        .sort((a, b) => Number(a.dataset.index) - Number(b.dataset.index))
        .forEach((i) => {
          i.classList.remove('is-chosen');
          (listEl.querySelector(`[data-eob-picker-group="${i.dataset.group || 'all'}"]`) || groups[0]).appendChild(i);
        });
      this.updatePickerHeadings(listEl);

      if (anchor) listEl.scrollTop += anchor.getBoundingClientRect().top - pinnedBottom() - offset;
    }

    updatePickerCount() {
      const max = this.samplesConfig.max;
      const boxes = [...this.picker.querySelectorAll('[data-eob-sample]')];
      const count = boxes.filter((b) => b.checked).length;
      boxes.forEach((b) => {
        b.disabled = !b.checked && count >= max;
        b.closest('.eob-samples__item').classList.toggle('is-disabled', b.disabled);
      });
      this.picker.querySelector('[data-eob-picker-count]').textContent = `${count}/${max}`;

      // "Update samples" when changing ones already in the bag; otherwise "Add 3 samples".
      const confirm = this.picker.querySelector('[data-eob-picker-confirm]');
      const d = confirm.dataset;
      let label;
      if (this.sampleLines().length) label = d.updateLabel;
      else if (count === 1) label = d.addOneLabel;
      else if (count === 0) label = (d.addLabel || '').replace(/\s*\{count\}\s*/, ' ').trim();
      else label = fill(d.addLabel, { count });
      confirm.textContent = label;
      confirm.disabled = count === 0 && !this.sampleLines().length;
    }

    cancelPicker() {
      store.set('samples-dismissed', '1');
      this.closeModal(this.picker);
    }

    confirmPicker() {
      const chosen = [...this.picker.querySelectorAll('[data-eob-sample]:checked')].map((b) => Number(b.value));
      const current = this.sampleLines();
      const updates = {};
      current.forEach((l) => { if (!chosen.includes(l.variantId)) updates[l.key] = 0; });
      const adds = chosen
        .filter((id) => !current.some((l) => l.variantId === id))
        .map((id) => ({ id, quantity: 1, properties: { _eob_sample: '1' } }));

      this.closeModal(this.picker);
      this.enqueue(async () => {
        this.setBusy(true);
        try {
          if (Object.keys(updates).length) await this.request('update', { updates });
          if (adds.length) await this.request('add', { items: adds });
          await this.reconcile();
        } finally {
          this.setBusy(false);
        }
      });
    }

    maybeAutoOpenPicker() {
      const cfg = this.samplesConfig;
      if (!cfg.autoOpen || store.get('samples-dismissed')) return;
      if (!this.samplesUnlocked() || this.sampleLines().length) return;
      this.openPicker();
    }

    /* ----- events ----- */

    onClick(evt) {
      const t = evt.target;
      if (t.closest('[data-eob-close]')) {
        this.close();
        return;
      }
      const removeBtn = t.closest('[data-eob-remove]');
      if (removeBtn) {
        this.removeLine(removeBtn.closest('.eob-line, [data-eob-slot]'));
        return;
      }
      if (t.closest('[data-eob-open-picker]')) {
        this.openPicker();
        return;
      }
      if (t.closest('[data-eob-picker-cancel]')) {
        this.cancelPicker();
        return;
      }
      if (t.closest('[data-eob-picker-confirm]')) {
        this.confirmPicker();
        return;
      }
      if (evt.target === this.picker) {
        this.cancelPicker();
        return;
      }
      if (evt.target === this.reminder) {
        this.closeModal(this.reminder);
        return;
      }

      const checkout = t.closest('[data-eob-checkout]');
      if (checkout && this.shouldRemind()) {
        evt.preventDefault();
        this.showReminder(checkout.href);
        return;
      }
      if (t.closest('[data-eob-reminder-choose]')) {
        this.closeModal(this.reminder);
        this.openPicker();
        return;
      }
      if (t.closest('[data-eob-reminder-skip]')) {
        window.location.href = this.reminderHref;
        return;
      }

      const step = t.closest('[data-eob-qty-step]');
      if (step) {
        const line = step.closest('.eob-line');
        const input = line.querySelector('[data-eob-qty-input]');
        const qty = Math.max(0, Number(input.value) + Number(step.dataset.eobQtyStep));
        input.value = qty;
        this.changeQuantity(line, qty);
        return;
      }
      const code = t.closest('[data-eob-remove-code]');
      if (code) {
        this.removeDiscount(code.dataset.eobRemoveCode);
        return;
      }
      const recsStep = t.closest('[data-eob-recs-step]');
      if (recsStep) this.scrollRecs(Number(recsStep.dataset.eobRecsStep));
    }

    onChange(evt) {
      if (evt.target.matches('[data-eob-sample]')) {
        const item = evt.target.closest('.eob-samples__item');
        item.dataset.chosenAt = evt.target.checked ? Date.now() : '';
        this.arrangePicker(item);
        this.updatePickerCount();
        return;
      }
      if (evt.target.matches('[data-eob-qty-input]')) {
        const line = evt.target.closest('.eob-line');
        this.changeQuantity(line, Math.max(0, parseInt(evt.target.value, 10) || 0));
      }
    }

    onSubmit(evt) {
      const form = evt.target;
      if (form.matches('[data-eob-discount-form]')) {
        evt.preventDefault();
        this.applyDiscount(form.querySelector('[name="discount"]').value.trim());
      } else if (form.matches('[data-eob-recs-form]')) {
        evt.preventDefault();
        const id = Number(form.querySelector('[name="id"]').value);
        // A "Complete your routine" card, or a product on the empty cart page.
        const item = form.closest('[data-product-id]');
        const btn = form.querySelector('button');
        btn.disabled = true;
        btn.classList.add('is-adding');
        this.enqueue(async () => {
          try {
            this.justAddedProduct = item.dataset.productId;
            await this.request('add', { items: [{ id, quantity: 1 }] });
            await this.reconcile();
            this.showAddedLine();
            this.maybeAutoOpenPicker();
          } catch (e) {
            this.justAddedProduct = null;
            btn.disabled = false;
            btn.classList.remove('is-adding');
          }
        });
      }
    }

    /** After adding from "Complete your routine": scroll up so the new line in the bag shows. */
    showAddedLine() {
      const scroller = !this.isPage && this.querySelector('.eob-cart__scroll');
      if (scroller) scroller.scrollTo({ top: 0, behavior: 'smooth' });
      // The cart page: back to the top, so the site's sticky header doesn't cover the new line.
      else window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
    }

    removeLine(line) {
      const key = line.dataset.key;
      const variantId = line.dataset.variantId;
      const body = { updates: { [key]: 0 } };
      if (line.dataset.kind === 'gift' && this.managedGiftIds().has(Number(variantId))) {
        const declined = new Set(this.ctx.declinedList);
        declined.add(variantId);
        body.attributes = { _eob_declined: [...declined].join(',') };
      }
      line.classList.add('is-removing');
      this.enqueue(async () => {
        this.setBusy(true);
        try {
          await this.request('update', body);
          await this.reconcile();
        } catch (e) {
          line.classList.remove('is-removing');
          this.showLineError(line, e.message);
        } finally {
          this.setBusy(false);
        }
      });
    }

    changeQuantity(line, quantity) {
      clearTimeout(line.eobTimer);
      line.eobTimer = setTimeout(() => {
        this.enqueue(async () => {
          this.setBusy(true);
          try {
            await this.request('change', { id: line.dataset.key, quantity });
            await this.reconcile();
          } catch (e) {
            const fresh = this.querySelector(`.eob-line[data-key="${line.dataset.key}"]`) || line;
            this.showLineError(fresh, e.message);
            await this.fetchSections();
          } finally {
            this.setBusy(false);
          }
        });
      }, 350);
    }

    showLineError(line, message) {
      const el = line && line.querySelector('[data-eob-line-error]');
      if (!el) return;
      el.textContent = message || this.ctx.texts.error;
      el.hidden = false;
    }

    currentCodes() {
      return [...this.querySelectorAll('[data-eob-remove-code]')].map((b) => b.dataset.eobRemoveCode);
    }

    applyDiscount(code) {
      if (!code) return;
      const errorEl = () => this.querySelector('[data-eob-discount-error]');
      this.enqueue(async () => {
        this.setBusy(true);
        try {
          const codes = [...new Set([...this.currentCodes(), code])];
          const cart = await this.request('update', { discount: codes.join(',') });
          const entry = (cart.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
          if (!entry || !entry.applicable) {
            const el = errorEl();
            if (el) {
              el.textContent = this.ctx.texts.discountInvalid;
              el.hidden = false;
            }
            // Drop a code that doesn't apply so it doesn't linger on the cart.
            if (entry) await this.request('update', { discount: this.currentCodes().filter((c) => c.toLowerCase() !== code.toLowerCase()).join(',') });
          }
          await this.reconcile();
        } finally {
          this.setBusy(false);
        }
      });
    }

    removeDiscount(code) {
      this.enqueue(async () => {
        this.setBusy(true);
        try {
          await this.request('update', { discount: this.currentCodes().filter((c) => c !== code).join(',') });
          await this.reconcile();
        } finally {
          this.setBusy(false);
        }
      });
    }

    /* ----- checkout reminder ----- */

    shouldRemind() {
      const reminder = this.samplesConfig.reminder || {};
      return !!reminder.enabled
        && this.samplesUnlocked()
        && !this.sampleLines().length
        && !store.get('samples-reminded');
    }

    showReminder(href) {
      const cfg = this.samplesConfig;
      const reminder = cfg.reminder || {};
      const tokens = { max: cfg.max };
      this.reminderHref = href;
      store.set('samples-reminded', '1');
      this.reminder.querySelector('[data-eob-reminder-title]').textContent = fill(reminder.title, tokens);
      this.reminder.querySelector('[data-eob-reminder-text]').textContent = fill(reminder.text, tokens);
      this.reminder.querySelector('[data-eob-reminder-choose]').textContent = fill(reminder.choose, tokens);
      this.reminder.querySelector('[data-eob-reminder-skip]').textContent = fill(reminder.skip, tokens);
      this.openModal(this.reminder);
    }
  }

  customElements.define('eob-cart-drawer', EobCartDrawer);
  window.EobCart = {
    open: () => document.querySelector('eob-cart-drawer')?.open(),
    refresh: () => document.querySelector('eob-cart-drawer')?.refresh()
  };
})();
